import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  fetchCompleteSessionData,
  fetchSessionDetails,
  fetchSessionPlotData,
  selectSessionData,
  selectSessionDetails,
  selectSessionCoordinates,
  selectSessionPlotData,
  selectIsSessionLoading,
  selectSessionError,
  selectIsSessionCached,
  selectHasThirdPartyData,
} from './sessionsSlice';

interface UseSessionDataProps {
  sessionId: string;
  sensorId: string;
}

export const useSessionData = ({ sessionId, sensorId }: UseSessionDataProps) => {
  const dispatch = useAppDispatch();

  const sessionData = useAppSelector(state => selectSessionData(state, sessionId));
  const sessionDetails = useAppSelector(state => selectSessionDetails(state, sessionId));
  const coordinates = useAppSelector(state => selectSessionCoordinates(state, sessionId));
  const plotData = useAppSelector(state => selectSessionPlotData(state, sessionId));
  const isLoading = useAppSelector(state => selectIsSessionLoading(state, sessionId));
  const error = useAppSelector(state => selectSessionError(state, sessionId));
  const isCached = useAppSelector(state => selectIsSessionCached(state, sessionId));
  const hasThirdPartyData = useAppSelector(state => selectHasThirdPartyData(state, sessionId));

  useEffect(() => {
    // Initial load for session details & first sensor plot data
    if (sessionId && sensorId && !isCached && !isLoading) {
      dispatch(fetchCompleteSessionData({ sessionId, sensorId }));
    }
  }, [dispatch, sessionId, sensorId, isCached, isLoading]);

  return {
    sessionData,
    sessionDetails,
    coordinates,
    plotData,
    hasThirdPartyData,
    isLoading,
    error,
    isCached,
  };
};

// Hook for just session details (for components that only need basic info)
export const useSessionDetails = (sessionId: string) => {
  const dispatch = useAppDispatch();

  const sessionDetails = useAppSelector(state => selectSessionDetails(state, sessionId));
  const isLoading = useAppSelector(state => selectIsSessionLoading(state, sessionId));
  const error = useAppSelector(state => selectSessionError(state, sessionId));
  const isCached = useAppSelector(state => selectIsSessionCached(state, sessionId));

  useEffect(() => {
    // Only fetch details if not cached
    if (sessionId && !sessionDetails && !isLoading && !isCached) {
      dispatch(fetchSessionDetails(sessionId));
    }
  }, [dispatch, sessionId, sessionDetails, isLoading, isCached]);

  return {
    sessionDetails,
    isLoading,
    error,
    isCached,
  };
};
